import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import { categories } from '@/mocks/categories';
import type { ArticleData } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.living';

const relatedArticlesSidebar = [
  { title: 'Guida Itaobuy Sheet 2026', desc: 'La guida completa per trovare prodotti in modo intelligente.', slug: '/guida-itaobuy-sheet-2026' },
  { title: 'Guida alla Sicurezza', desc: 'Tutto quello che devi sapere per acquistare in sicurezza.', slug: '/guida-sicurezza-itaobuy' },
  { title: 'Guida per Principianti', desc: 'I primi passi nel mondo di Itaobuy Spreadsheet.', slug: '/come-usare-itaobuy-principianti' },
  { title: 'Strategie di Ricerca', desc: 'Metodi avanzati per trovare i prodotti migliori.', slug: '/strategie-ricerca-prodotti' },
  { title: 'Errori Comuni', desc: 'Cosa evitare per acquistare senza sorprese.', slug: '/errori-comuni-itaobuy' },
];

const relatedCards = [
  {
    title: 'Guida Itaobuy Sheet 2026: come trovare prodotti in modo intelligente',
    desc: 'Scopri come sfruttare al meglio lo spreadsheet per analizzare i prodotti e prendere decisioni informate.',
    slug: '/guida-itaobuy-sheet-2026',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20product%20display%20with%20fashion%20items%20on%20warm%20sand%20background%2C%20soft%20natural%20light%2C%20clean%20editorial%20composition&width=600&height=300&seq=related-card-guide&orientation=landscape',
  },
  {
    title: 'Itaobuy Spreadsheet sicurezza: cosa devi sapere',
    desc: 'Tutte le informazioni sulla sicurezza e i consigli pratici per acquistare in modo protetto.',
    slug: '/guida-sicurezza-itaobuy',
    image: 'https://readdy.ai/api/search-image?query=Secure%20shopping%20concept%20with%20padlock%20and%20product%20catalog%20on%20warm%20cream%20background%2C%20minimalist%20editorial%20style%2C%20trustworthy%20atmosphere&width=600&height=300&seq=related-card-sicurezza&orientation=landscape',
  },
  {
    title: 'Strategie ricerca prodotti con Itaobuy',
    desc: 'Come confrontare efficacemente diversi prodotti utilizzando lo spreadsheet organizzato.',
    slug: '/strategie-ricerca-prodotti',
    image: 'https://readdy.ai/api/search-image?query=Data%20analysis%20and%20product%20comparison%20workspace%20on%20light%20warm%20background%2C%20modern%20minimalist%20style%2C%20clean%20editorial%20photography&width=600&height=300&seq=related-card-strategie&orientation=landscape',
  },
];

interface ArticleLayoutProps {
  article: ArticleData;
  currentSlug: string;
}

function renderContent(text: string) {
  return <span dangerouslySetInnerHTML={{ __html: text }} />;
}

function generateArticleJsonLd(article: ArticleData, slug: string) {
  const pageUrl = `${siteUrl}${slug}`;
  const plainText = article.sections
    .filter((s) => s.type === 'p')
    .map((s) => s.text || '')
    .join(' ')
    .replace(/<[^>]*>/g, '')
    .substring(0, 5000);

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.h1,
    description: article.seoDescription,
    image: article.heroImage,
    datePublished: '2026-07-14',
    dateModified: '2026-07-14',
    author: {
      '@type': 'Organization',
      name: 'Itaobuy Spreadsheet Italia',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Itaobuy Spreadsheet Italia',
      url: siteUrl,
    },
    url: pageUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    articleBody: plainText,
    inLanguage: 'it-IT',
  };
}

export default function ArticleLayout({ article, currentSlug }: ArticleLayoutProps) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const filteredRelated = relatedCards.filter((c) => c.slug !== currentSlug).slice(0, 3);
  const filteredSidebar = relatedArticlesSidebar.filter((a) => a.slug !== currentSlug);

  return (
    <>
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(generateArticleJsonLd(article, currentSlug)) }}
      />
      <main className="pt-20 pb-16 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1280px] mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <Link to="/guida-itaobuy-sheet-2026" className="hover:text-accent-600 transition-colors cursor-pointer">Guide</Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-foreground-600">{article.breadcrumb}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <article className="flex-1 max-w-none lg:max-w-[75%]">
                <header className="mb-10">
                  <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                    {article.badge}
                  </span>
                  <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                    {article.h1}
                  </h1>
                  <p className="text-foreground-500 text-sm">
                    Ultimo aggiornamento: {article.date} &middot; Tempo di lettura: {article.readTime}
                  </p>
                </header>

                <div className="rounded-[20px] overflow-hidden mb-10">
                  <img
                    src={article.heroImage}
                    alt={article.h1}
                    className="w-full h-auto object-cover"
                  />
                </div>

                <div className="text-foreground-700 text-base leading-[1.8] space-y-6">
                  {article.sections.map((section, idx) => {
                    if (section.type === 'h2') {
                      return (
                        <h2 key={idx} className="font-heading text-2xl font-bold text-foreground-900 mt-10 mb-4">
                          {section.text}
                        </h2>
                      );
                    }
                    if (section.type === 'h3') {
                      return (
                        <h3 key={idx} className="font-semibold text-foreground-800 mt-6 mb-2">
                          {section.text}
                        </h3>
                      );
                    }
                    if (section.type === 'p') {
                      return (
                        <p key={idx}>
                          {renderContent(section.text || '')}
                        </p>
                      );
                    }
                    if (section.type === 'ul' && section.items) {
                      return (
                        <ul key={idx} className="list-disc pl-5 space-y-2">
                          {section.items.map((item, i) => (
                            <li key={i}>{renderContent(item)}</li>
                          ))}
                        </ul>
                      );
                    }
                    if (section.type === 'ol' && section.items) {
                      return (
                        <ol key={idx} className="list-decimal pl-5 space-y-3">
                          {section.items.map((item, i) => (
                            <li key={i}>{renderContent(item)}</li>
                          ))}
                        </ol>
                      );
                    }
                    return null;
                  })}
                </div>

                <div className="mt-12 pt-8 border-t border-background-200">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i> Torna alla Home
                  </Link>
                </div>
              </article>

              <aside className="lg:w-[25%]">
                <div className="sticky top-24 space-y-8">
                  <div className="bg-background-100 rounded-2xl p-5">
                    <h3 className="font-semibold text-foreground-800 mb-4 flex items-center gap-2">
                      <i className="ri-price-tag-3-line text-accent-500"></i> Categorie
                    </h3>
                    <div className="space-y-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.seq}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="block px-3 py-2 rounded-lg text-sm text-foreground-600 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                        >
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-5">
                    <h3 className="font-semibold text-foreground-800 mb-4 flex items-center gap-2">
                      <i className="ri-article-line text-accent-500"></i> Guide correlate
                    </h3>
                    <div className="space-y-3">
                      {filteredSidebar.map((guide, idx) => (
                        <Link
                          key={idx}
                          to={guide.slug}
                          className="block group cursor-pointer"
                        >
                          <h4 className="text-sm font-medium text-foreground-700 group-hover:text-accent-600 transition-colors">
                            {guide.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-1 line-clamp-2">{guide.desc}</p>
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="bg-primary-500 rounded-2xl p-5 text-center">
                    <p className="text-background-50 text-sm font-medium mb-3">
                      Vuoi scoprire i migliori prodotti?
                    </p>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-block px-5 py-2.5 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Visita itaobuy sheet
                    </a>
                  </div>
                </div>
              </aside>
            </div>

            <div className="mt-16">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">Articoli correlati</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {filteredRelated.map((card, idx) => (
                  <Link
                    key={idx}
                    to={card.slug}
                    className="block bg-background-100 rounded-2xl p-5 hover:bg-accent-50/60 transition-colors cursor-pointer"
                  >
                    <div className="w-full h-32 rounded-xl overflow-hidden mb-4">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">{card.title}</h4>
                    <p className="text-xs text-foreground-500 line-clamp-2">{card.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}